/**
 * Client-side validation for Klaviyo embed forms in the footer newsletter area (email + phone).
 * Phone rules match assets/email-signup.js (E.164 via market country).
 * Uses Theme.translations (blocks.email_signup.*) from snippets/scripts.liquid.
 */
// @ts-nocheck

function newsletterT(key) {
  if (typeof Theme !== 'undefined' && Theme.translations && Theme.translations[key]) {
    return Theme.translations[key];
  }
  return '';
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Matches `icon-error.svg` with stroke using currentColor for theme error styling */
const ERROR_ICON_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8.00001 11.3334V11.34M8.00001 8.66671L8.00001 4.33337M1.33334 8.00004C1.33334 4.31814 4.31811 1.33337 8.00001 1.33337C11.6819 1.33337 14.6667 4.31814 14.6667 8.00004C14.6667 11.6819 11.6819 14.6667 8.00001 14.6667C4.31811 14.6667 1.33334 11.6819 1.33334 8.00004Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** Country-agnostic phone normalizer → E.164. Mirrors `assets/email-signup.js`. */
function normalizePhoneToE164(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';

  let cleaned = trimmed.replace(/[\s\-().]/g, '');

  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}`;
  }

  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1).replace(/\D/g, '');
    if (/^[1-9]\d{7,14}$/.test(digits)) {
      return `+${digits}`;
    }
    return '';
  }

  const digitsOnly = cleaned.replace(/\D/g, '');
  if (/^[1-9]\d{7,14}$/.test(digitsOnly)) {
    return `+${digitsOnly}`;
  }
  return '';
}

function isE164String(value) {
  return typeof value === 'string' && /^\+[1-9]\d{7,14}$/.test(value);
}

function findLabelForInput(input) {
  if (!(input instanceof HTMLInputElement)) return null;
  if (input.id && typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    const sel = `label[for="${CSS.escape(input.id)}"]`;
    const byFor = input.form?.querySelector(sel) || document.querySelector(sel);
    if (byFor) return byFor;
  }
  const wrapped = input.closest('label');
  if (wrapped) return wrapped;
  const prev = input.previousElementSibling;
  if (prev instanceof HTMLLabelElement) return prev;
  return null;
}

function isFooterKlaviyoNewsletterForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  if (!form.classList.contains('klaviyo-form')) return false;
  return Boolean(form.closest('.footer-content .newsletter-form'));
}

function findEmailInput(form) {
  return (
    form.querySelector('input[type="email"]') ||
    form.querySelector('input[name="email"]') ||
    form.querySelector('input[autocomplete="email"]')
  );
}

function findPhoneInput(form) {
  const direct =
    form.querySelector('input[type="tel"]') ||
    form.querySelector('input[name="phone"]') ||
    form.querySelector('input[name="phone_number"]') ||
    form.querySelector('input[autocomplete="tel"]');
  if (direct) return direct;

  for (const input of form.querySelectorAll('input')) {
    if (!(input instanceof HTMLInputElement)) continue;
    const t = (input.type || '').toLowerCase();
    if (t === 'hidden' || t === 'submit' || t === 'button' || t === 'checkbox' || t === 'radio') {
      continue;
    }
    const nm = (input.name || '').toLowerCase();
    const id = (input.id || '').toLowerCase();
    const ac = (input.getAttribute('autocomplete') || '').toLowerCase();
    if (nm.includes('phone') || id.includes('phone') || ac.includes('tel') || ac === 'tel-national') {
      return input;
    }
  }
  return null;
}

function getNewsletterScope(form) {
  return form.closest('.newsletter-form') || form.closest('.footer-content');
}

/** Insert the message node immediately after the anchor input (email or phone). */
function placeErrorBelowInput(form, anchorInput, container) {
  if (!form || !container) {
    return;
  }

  if (!anchorInput) {
    const scope = getNewsletterScope(form);
    if (scope && !scope.contains(container)) {
      scope.appendChild(container);
    }
    return;
  }

  if (container.previousElementSibling === anchorInput) {
    return;
  }

  anchorInput.after(container);
}

function getOrCreateErrorContainer(scope, form, anchorInput) {
  let el = scope.querySelector('[data-klaviyo-newsletter-validation-error]');
  const resolvedAnchor = anchorInput || findEmailInput(form);
  if (!el) {
    el = document.createElement('div');
    el.setAttribute('data-klaviyo-newsletter-validation-error', '');
    el.className = 'klaviyo-newsletter-validation-message';
    el.setAttribute('role', 'alert');
    el.hidden = true;
    const iconWrap = document.createElement('span');
    iconWrap.className = 'klaviyo-newsletter-validation-message__icon';
    iconWrap.innerHTML = ERROR_ICON_SVG;
    const p = document.createElement('p');
    p.className = 'klaviyo-newsletter-validation-message__text';
    el.appendChild(iconWrap);
    el.appendChild(p);
    if (form) {
      placeErrorBelowInput(form, resolvedAnchor, el);
    } else {
      scope.appendChild(el);
    }
  } else if (!el.querySelector('.klaviyo-newsletter-validation-message__icon')) {
    const iconWrap = document.createElement('span');
    iconWrap.className = 'klaviyo-newsletter-validation-message__icon';
    iconWrap.innerHTML = ERROR_ICON_SVG;
    const text = el.querySelector('.klaviyo-newsletter-validation-message__text');
    if (text) {
      el.insertBefore(iconWrap, text);
    } else {
      el.insertBefore(iconWrap, el.firstChild);
    }
  }
  if (form) {
    placeErrorBelowInput(form, resolvedAnchor, el);
  }
  return el;
}

function setKlaviyoFieldInvalidState(input, invalid) {
  if (!input) return;
  const type = (input.getAttribute('type') || '').toLowerCase();
  const name = (input.getAttribute('name') || '').toLowerCase();
  const emailClass = 'klaviyo-field--email-invalid';
  const phoneClass = 'klaviyo-field--phone-invalid';

  if (type === 'email' || name === 'email') {
    if (invalid) input.classList.add(emailClass);
    else input.classList.remove(emailClass);
  } else {
    if (invalid) input.classList.add(phoneClass);
    else input.classList.remove(phoneClass);
  }

  const lab = findLabelForInput(input);
  if (lab) {
    if (invalid) lab.classList.add('klaviyo-label--error');
    else lab.classList.remove('klaviyo-label--error');
  }
}

function clearKlaviyoFieldErrorVisuals(form) {
  setKlaviyoFieldInvalidState(findEmailInput(form), false);
  setKlaviyoFieldInvalidState(findPhoneInput(form), false);
}

function showKlaviyoNewsletterError(scope, message, form, failedInput) {
  const anchor = failedInput || findEmailInput(form);
  const container = getOrCreateErrorContainer(scope, form, anchor);
  const textEl = container.querySelector('.klaviyo-newsletter-validation-message__text');
  if (textEl) {
    textEl.textContent = message;
  }
  container.hidden = false;
  scope.classList.add('klaviyo-newsletter--validation-error');
  if (form) {
    clearKlaviyoFieldErrorVisuals(form);
    if (failedInput) {
      setKlaviyoFieldInvalidState(failedInput, true);
    }
  }
}

function hideKlaviyoNewsletterError(scope) {
  const container = scope.querySelector('[data-klaviyo-newsletter-validation-error]');
  if (container) {
    container.hidden = true;
  }
  scope.classList.remove('klaviyo-newsletter--validation-error');
  const form = scope.querySelector('form.klaviyo-form');
  if (form) {
    clearKlaviyoFieldErrorVisuals(form);
  }
}

function validateFooterKlaviyoNewsletter(form) {
  const emailInput = findEmailInput(form);
  const emailValue = emailInput ? emailInput.value.trim() : '';

  if (!emailValue) {
    const msg = newsletterT('newsletter_required_email') || newsletterT('newsletter_required_field');
    return { ok: false, message: msg, failedInput: emailInput };
  }
  if (!EMAIL_REGEX.test(emailValue)) {
    return { ok: false, message: newsletterT('newsletter_invalid_email'), failedInput: emailInput };
  }

  const phoneInput = findPhoneInput(form);
  if (!phoneInput) {
    return { ok: true };
  }

  const phoneValue = phoneInput.value.trim();
  if (!phoneValue) {
    const msg = newsletterT('newsletter_required_phone') || newsletterT('newsletter_required_field');
    return { ok: false, message: msg, failedInput: phoneInput };
  }

  const e164 = normalizePhoneToE164(phoneValue);
  if (!isE164String(e164)) {
    return {
      ok: false,
      message: newsletterT('newsletter_required_phone') || newsletterT('newsletter_required_field'),
      failedInput: phoneInput,
    };
  }

  return { ok: true };
}

function runFooterKlaviyoValidation(event, form) {
  const scope = getNewsletterScope(form);
  if (!scope) return true;

  const result = validateFooterKlaviyoNewsletter(form);
  if (result.ok) {
    hideKlaviyoNewsletterError(scope);
    return true;
  }

  if (event && typeof event.preventDefault === 'function') {
    event.preventDefault();
    event.stopPropagation();
  }
  const msg =
    result.message && result.message.length > 0 ? result.message : newsletterT('newsletter_error_generic');
  showKlaviyoNewsletterError(scope, msg, form, result.failedInput);
  if (result.failedInput) {
    result.failedInput.focus({ preventScroll: false });
  }
  return false;
}

function onFooterKlaviyoSubmit(event) {
  const form = event.target;
  if (!isFooterKlaviyoNewsletterForm(form)) return;

  if (runFooterKlaviyoValidation(event, form)) {
    return;
  }
}

/** Klaviyo often handles the button before a native submit event; validate in capture phase first. */
function onFooterKlaviyoSubmitButtonClick(event) {
  const btn =
    event.target && event.target.closest
      ? event.target.closest(
          'button[type="submit"], input[type="submit"], button:not([type]):not([role="combobox"])'
        )
      : null;
  if (!btn) return;
  const form = btn.form;
  if (!form || !isFooterKlaviyoNewsletterForm(form)) return;

  if (runFooterKlaviyoValidation(event, form)) {
    return;
  }
  if (typeof event.stopImmediatePropagation === 'function') {
    event.stopImmediatePropagation();
  }
}

function onFooterKlaviyoInput(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  const form = target.form;
  if (!form || !isFooterKlaviyoNewsletterForm(form)) return;
  const scope = getNewsletterScope(form);
  if (scope) {
    hideKlaviyoNewsletterError(scope);
  }
}

document.addEventListener('submit', onFooterKlaviyoSubmit, true);
document.addEventListener('click', onFooterKlaviyoSubmitButtonClick, true);
document.addEventListener('input', onFooterKlaviyoInput, true);
